import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { setAdmin, logoutAdmin } from "../../redux/adminSlice";
import profileSettingRequests from "../../api/ProfileSettingRequests";
import Navbar from "../../components/dashboard/Navbar";
import {
  User,
  Lock,
  Mail,
  Phone,
  Edit3,
  Save,
  X,
  Eye,
  EyeOff,
  Shield,
  Settings,
  CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ProfileSetting = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminData = useSelector((state) => state.admin.txtconnect_ng_admin);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    if (adminData) {
      setProfileData({
        first_name: adminData.first_name || "",
        last_name: adminData.last_name || "",
        email: adminData.email || "",
        phone_number: adminData.phone_number || "",
      });
    }
  }, [adminData]);

  const validateProfileForm = () => {
    const newErrors = {};

    if (!profileData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!profileData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (!profileData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else {
      if (profileData.phone_number.startsWith("233")) {
        if (!/^233\d{9}$/.test(profileData.phone_number)) {
          newErrors.phone_number =
            "Phone number starting with 233 must be 12 digits (233 + 9 numbers)";
        }
      } else if (profileData.phone_number.startsWith("234")) {
        if (!/^234\d{10}$/.test(profileData.phone_number)) {
          newErrors.phone_number =
            "Phone number starting with 234 must be 13 digits (234 + 10 numbers)";
        }
      } else {
        newErrors.phone_number = "Phone number must start with 233 or 234";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) return "";
    if (phone.startsWith("233")) {
      if (!/^233\d{9}$/.test(phone)) {
        return "Phone number starting with 233 must be 12 digits (233 + 9 numbers)";
      }
    } else if (phone.startsWith("234")) {
      if (!/^234\d{10}$/.test(phone)) {
        return "Phone number starting with 234 must be 13 digits (234 + 10 numbers)";
      }
    } else {
      return "Phone number must start with 233 or 234";
    }
    return "";
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.current_password) {
      newErrors.current_password = "Current password is required";
    }

    if (!passwordData.new_password) {
      newErrors.new_password = "New password is required";
    } else if (passwordData.new_password.length < 6) {
      newErrors.new_password = "Password must be at least 6 characters";
    }

    if (!passwordData.confirm_password) {
      newErrors.confirm_password = "Please confirm your password";
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));

    let validationError = "";
    if (name === "phone_number") {
      validationError = validatePhone(value);
    }

    setErrors((prev) => ({
      ...prev,
      [name]: validationError,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
    let validationError = "";
    if (name === "new_password") {
      if (value && value.length < 6) {
        validationError = "Password must be at least 6 characters";
      }
    } else if (name === "confirm_password") {
      if (value && value !== passwordData.new_password) {
        validationError = "Passwords do not match";
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: validationError,
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!validateProfileForm()) return;

    setIsLoading(true);
    try {
      const response = await profileSettingRequests.updateProfile(profileData);
      if (
        response.data &&
        response.data.data.status_code === "000" &&
        !response.data.data.in_error
      ) {
        const updatedProfileData = response.data.data.data;

        try {
          dispatch(
            setAdmin({
              adminData: {
                ...adminData,
                first_name: updatedProfileData.first_name,
                last_name: updatedProfileData.last_name,
                email: updatedProfileData.email,
                phone_number: updatedProfileData.phone_number,
              },
              token: localStorage.getItem("txtconnect_ng_admin_token"),
            })
          );
          setProfileData((prev) => ({
            ...prev,
            first_name: updatedProfileData.first_name,
            last_name: updatedProfileData.last_name,
            email: updatedProfileData.email,
            phone_number: updatedProfileData.phone_number,
          }));

          toast.success(
            response.data.data.reason || "Profile updated successfully"
          );
          setIsEditing(false);
        } catch (dispatchError) {
          toast.success("Profile updated successfully");
          setIsEditing(false);
        }
      } else {
        toast.error(
          response.data?.data?.reason ||
            response.data?.data?.message ||
            "Failed to update profile"
        );
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    setIsLoading(true);
    try {
      const response = await profileSettingRequests.updatePassword({
        old_password: passwordData.current_password,
        password: passwordData.new_password,
        password_confirmation: passwordData.confirm_password,
      });

      if (response.data?.data?.status_code === "000") {
        toast.success(
          response.data?.data?.reason || "Password Reset Successfully"
        );
        setPasswordData({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
        setActiveTab("profile");
        dispatch(logoutAdmin());
        navigate("/login");
      } else {
        toast.error(response.data?.data?.reason || "Failed to update password");
      }
    } catch (error) {
      toast.error("Failed to update password");
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const tabVariants = {
    hidden: {
      opacity: 0,
      x: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      x: -20,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  const tabButtonVariants = {
    hover: {
      scale: 1.02,
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.98,
    },
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
        <div className="px-2 py-4 mx-auto w-full max-w-full md:max-w-8xl sm:px-4 sm:py-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex gap-3 items-center mb-4 sm:gap-4">
              <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-lg sm:w-16 sm:h-16">
                <Settings className="w-7 h-7 text-white sm:w-8 sm:h-8" />
              </div>
              <div>
                <h1 className="mb-1 text-2xl font-bold text-gray-900 sm:mb-2 sm:text-3xl">
                  Profile Settings
                </h1>
                <p className="text-sm text-gray-600 sm:text-base">
                  Manage your account information and security preferences
                </p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mx-auto mb-6 w-full max-w-full md:max-w-8xl sm:mb-8">
            <div className="flex flex-col gap-2 items-stretch p-1 bg-white rounded-2xl border border-gray-100 shadow-sm sm:flex-row sm:items-center sm:p-2 sm:gap-0">
              <motion.button
                onClick={() => setActiveTab("profile")}
                variants={tabButtonVariants}
                whileHover="hover"
                whileTap="tap"
                className={`w-full sm:w-auto flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all duration-200 ${
                  activeTab === "profile"
                    ? "text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                <User className="w-5 h-5" />
                <span className="text-sm sm:text-base">
                  Profile Information
                </span>
              </motion.button>
              <motion.button
                onClick={() => setActiveTab("password")}
                variants={tabButtonVariants}
                whileHover="hover"
                whileTap="tap"
                className={`w-full sm:w-auto flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all duration-200 ${
                  activeTab === "password"
                    ? "text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                <Lock className="w-5 h-5" />
                <span className="text-sm sm:text-base">Change Password</span>
              </motion.button>
            </div>
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === "profile" && (
              <motion.div
                key="profile"
                variants={tabVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="mx-auto w-full max-w-full md:max-w-8xl"
              >
                <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-xl sm:rounded-3xl">
                  {/* Header */}
                  <div className="p-4 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 sm:p-8">
                    <div className="flex gap-3 items-center sm:gap-4">
                      <div className="flex justify-center items-center w-12 h-12 rounded-2xl backdrop-blur-sm sm:w-16 sm:h-16 bg-white/20">
                        <User className="w-7 h-7 text-white sm:w-8 sm:h-8" />
                      </div>
                      <div>
                        <h2 className="mb-1 text-xl font-bold text-white sm:text-2xl">
                          Personal Information
                        </h2>
                        <p className="text-xs text-blue-100 sm:text-base">
                          Update your profile details and contact information
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 sm:p-8">
                    {!isEditing ? (
                      <div className="space-y-6">
                        {/* Profile Display */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 sm:gap-6">
                          <div className="space-y-2">
                            <div className="flex gap-2 items-center text-sm font-medium text-gray-500">
                              <User className="w-4 h-4" />
                              First Name
                            </div>
                            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                              <p className="font-semibold text-gray-900">
                                {profileData.first_name}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="flex gap-2 items-center text-sm font-medium text-gray-500">
                              <User className="w-4 h-4" />
                              Last Name
                            </div>
                            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                              <p className="font-semibold text-gray-900">
                                {profileData.last_name}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex gap-2 items-center text-sm font-medium text-gray-500">
                            <Mail className="w-4 h-4" />
                            Email Address
                          </div>
                          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <p className="font-semibold text-gray-900">
                              {profileData.email}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex gap-2 items-center text-sm font-medium text-gray-500">
                            <Phone className="w-4 h-4" />
                            Phone Number
                          </div>
                          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <p className="font-semibold text-gray-900">
                              {profileData.phone_number}
                            </p>
                          </div>
                        </div>

                        {/* Edit Button */}
                        <button
                          onClick={() => setIsEditing(true)}
                          className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl transition-all duration-200 hover:from-blue-700 hover:to-purple-700 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <Edit3 className="w-5 h-5" />
                          Edit Profile Information
                        </button>
                      </div>
                    ) : (
                      <form
                        onSubmit={handleProfileSubmit}
                        className="space-y-6"
                      >
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div className="space-y-2">
                            <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
                              <User className="w-4 h-4" />
                              First Name
                            </label>
                            <input
                              type="text"
                              name="first_name"
                              value={profileData.first_name}
                              onChange={handleProfileChange}
                              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                errors.first_name
                                  ? "border-red-300 bg-red-50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              placeholder="Enter your first name"
                            />
                            {errors.first_name && (
                              <p className="flex gap-1 items-center text-sm text-red-500">
                                <X className="w-4 h-4" />
                                {errors.first_name}
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
                              <User className="w-4 h-4" />
                              Last Name
                            </label>
                            <input
                              type="text"
                              name="last_name"
                              value={profileData.last_name}
                              onChange={handleProfileChange}
                              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                errors.last_name
                                  ? "border-red-300 bg-red-50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              placeholder="Enter your last name"
                            />
                            {errors.last_name && (
                              <p className="flex gap-1 items-center text-sm text-red-500">
                                <X className="w-4 h-4" />
                                {errors.last_name}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
                            <Mail className="w-4 h-4" />
                            Email Address
                          </label>
                          <div className="relative">
                            <input
                              type="email"
                              name="email"
                              value={profileData.email}
                              readOnly
                              disabled
                              className="px-4 py-3 pr-10 w-full text-gray-600 bg-gray-100 rounded-xl border border-gray-300 cursor-not-allowed"
                              placeholder="Email address (readonly)"
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <Mail className="w-5 h-5 text-gray-400" />
                            </div>
                          </div>
                          <div className="flex gap-2 items-start p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex-shrink-0 mt-0.5">
                              <svg
                                className="w-4 h-4 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <div className="text-sm text-blue-800">
                              <p className="font-medium">
                                Email addresses are read-only
                              </p>
                              <p className="mt-1">
                                To change your email address, please contact the
                                Super Administrator.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
                            <Phone className="w-4 h-4" />
                            Phone Number
                          </label>
                          <div className="relative">
                            <input
                              type="tel"
                              name="phone_number"
                              value={profileData.phone_number}
                              onChange={handleProfileChange}
                              className={`w-full px-4 py-3 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                                errors.phone_number
                                  ? "border-red-300 bg-red-50"
                                  : profileData.phone_number &&
                                    !errors.phone_number
                                  ? "border-red-300 bg-red-50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              placeholder="Enter your phone number (e.g., 233123456789 or 2341234567890)"
                            />
                            {profileData.phone_number && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                {errors.phone_number ? (
                                  <X className="w-5 h-5 text-red-500" />
                                ) : (
                                  <CheckCircle className="w-5 h-5 text-red-500" />
                                )}
                              </div>
                            )}
                          </div>
                          {errors.phone_number && (
                            <p className="flex gap-1 items-center text-sm text-red-500">
                              <X className="w-4 h-4" />
                              {errors.phone_number}
                            </p>
                          )}
                          {profileData.phone_number && !errors.phone_number && (
                            <p className="flex gap-1 items-center text-sm text-red-600">
                              <CheckCircle className="w-4 h-4" />
                              Valid phone number
                            </p>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-4 pt-4">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-red-600 to-red-600 text-white font-semibold rounded-xl transition-all duration-200 hover:from-red-700 hover:to-red-700 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isLoading ? (
                              <>
                                <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="w-5 h-5" />
                                Save Changes
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsEditing(false)}
                            className="flex flex-1 gap-3 justify-center items-center px-6 py-4 font-semibold text-gray-700 rounded-xl border border-gray-300 transition-all duration-200 hover:bg-gray-50 hover:border-gray-400"
                          >
                            <X className="w-5 h-5" />
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === "password" && (
              <motion.div
                key="password"
                variants={tabVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="mx-auto w-full max-w-full md:max-w-8xl"
              >
                <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-xl sm:rounded-3xl">
                  <div className="p-4 bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 sm:p-8">
                    <div className="flex gap-3 items-center sm:gap-4">
                      <div className="flex justify-center items-center w-12 h-12 rounded-2xl backdrop-blur-sm sm:w-16 sm:h-16 bg-white/20">
                        <Shield className="w-7 h-7 text-white sm:w-8 sm:h-8" />
                      </div>
                      <div>
                        <h2 className="mb-1 text-xl font-bold text-white sm:text-2xl">
                          Security Settings
                        </h2>
                        <p className="text-xs text-orange-100 sm:text-base">
                          Update your password and security preferences
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 sm:p-8">
                    <form onSubmit={handlePasswordSubmit} className="space-y-6">
                      {/* Current Password */}
                      <div className="space-y-2">
                        <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
                          <Lock className="w-4 h-4" />
                          Current Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.current ? "text" : "password"}
                            name="current_password"
                            value={passwordData.current_password}
                            onChange={handlePasswordChange}
                            className={`w-full px-4 py-3 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                              errors.current_password
                                ? "border-red-300 bg-red-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            placeholder="Enter your current password"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility("current")}
                            className="absolute right-3 top-1/2 text-gray-400 transform -translate-y-1/2 hover:text-gray-600"
                          >
                            {showPasswords.current ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        {errors.current_password && (
                          <p className="flex gap-1 items-center text-sm text-red-500">
                            <X className="w-4 h-4" />
                            {errors.current_password}
                          </p>
                        )}
                      </div>

                      {/* New Password */}
                      <div className="space-y-2">
                        <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
                          <Lock className="w-4 h-4" />
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.new ? "text" : "password"}
                            name="new_password"
                            value={passwordData.new_password}
                            onChange={handlePasswordChange}
                            className={`w-full px-4 py-3 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                              errors.new_password
                                ? "border-red-300 bg-red-50"
                                : passwordData.new_password &&
                                  !errors.new_password
                                ? "border-red-300 bg-red-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            placeholder="Enter your new password"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility("new")}
                            className="absolute right-3 top-1/2 text-gray-400 transform -translate-y-1/2 hover:text-gray-600"
                          >
                            {showPasswords.new ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        {errors.new_password && (
                          <p className="flex gap-1 items-center text-sm text-red-500">
                            <X className="w-4 h-4" />
                            {errors.new_password}
                          </p>
                        )}
                        {passwordData.new_password && !errors.new_password && (
                          <div className="flex gap-1 items-center text-sm text-red-600">
                            <CheckCircle className="w-4 h-4" />
                            Password meets requirements
                          </div>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div className="space-y-2">
                        <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
                          <Lock className="w-4 h-4" />
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.confirm ? "text" : "password"}
                            name="confirm_password"
                            value={passwordData.confirm_password}
                            onChange={handlePasswordChange}
                            className={`w-full px-4 py-3 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                              errors.confirm_password
                                ? "border-red-300 bg-red-50"
                                : passwordData.confirm_password &&
                                  !errors.confirm_password
                                ? "border-red-300 bg-red-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            placeholder="Confirm your new password"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility("confirm")}
                            className="absolute right-3 top-1/2 text-gray-400 transform -translate-y-1/2 hover:text-gray-600"
                          >
                            {showPasswords.confirm ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        {errors.confirm_password && (
                          <p className="flex gap-1 items-center text-sm text-red-500">
                            <X className="w-4 h-4" />
                            {errors.confirm_password}
                          </p>
                        )}
                        {passwordData.confirm_password &&
                          !errors.confirm_password && (
                            <div className="flex gap-1 items-center text-sm text-red-600">
                              <CheckCircle className="w-4 h-4" />
                              Passwords match
                            </div>
                          )}
                      </div>

                      {/* Password Requirements */}
                      <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                        <h4 className="mb-2 text-sm font-medium text-orange-800">
                          Password Requirements:
                        </h4>
                        <ul className="space-y-1 text-sm text-orange-700">
                          <li className="flex gap-2 items-center">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                passwordData.new_password?.length >= 6
                                  ? "bg-red-500"
                                  : "bg-orange-300"
                              }`}
                            ></div>
                            At least 6 characters long
                          </li>
                          <li className="flex gap-2 items-center">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                passwordData.new_password &&
                                passwordData.confirm_password &&
                                passwordData.new_password ===
                                  passwordData.confirm_password
                                  ? "bg-red-500"
                                  : "bg-orange-300"
                              }`}
                            ></div>
                            Passwords must match
                          </li>
                        </ul>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-4 pt-4">
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-xl transition-all duration-200 hover:from-orange-700 hover:to-red-700 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoading ? (
                            <>
                              <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white" />
                              Updating...
                            </>
                          ) : (
                            <>
                              <Shield className="w-5 h-5" />
                              Update Password
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPasswordData({
                              current_password: "",
                              new_password: "",
                              confirm_password: "",
                            });
                            setErrors({});
                          }}
                          className="flex flex-1 gap-3 justify-center items-center px-6 py-4 font-semibold text-gray-700 rounded-xl border border-gray-300 transition-all duration-200 hover:bg-gray-50 hover:border-gray-400"
                        >
                          <X className="w-5 h-5" />
                          Clear
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default ProfileSetting;
